import { useEntropyBrowser } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { useWebBrowserExtensionTransportEnabled } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/useWebBrowserExtensionAvailable.js';
import { WEB_EXTENSION_CONNECT_TRANSPORT } from '#pplx/frontend/comet-agent-core/types.js';
import { connectAsiViaPerplexityExtension } from '#pplx/frontend/comet/client/perplexityExtension.js';
import { convertBrowserColorToCustom, handleCloseTabs, handleGroupTabs, isCometBrowser, openTabAsync, sendSearchBrowserAsync, sendToolResultAsync, } from '#pplx/frontend/comet/client/utils.js';
import { createUploadUrl } from '#pplx/frontend/components/hooks/attachments.js';
import { REQUEST_ID_HEADER } from '#pplx/frontend/fetcher/constants.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useMemo } from 'react';
const safeJsonParse = (json) => {
    if (!json) {
        return {};
    }
    try {
        return JSON.parse(json);
    }
    catch {
        return {};
    }
};
export function useExecuteBrowserTool({ reason }) {
    const isComet = isCometBrowser();
    const cometBrowser = useEntropyBrowser();
    const webExtensionEnabled = useWebBrowserExtensionTransportEnabled();
    const connectAsi = useCallback((payload) => {
        if (payload.transport !== WEB_EXTENSION_CONNECT_TRANSPORT) {
            void cometBrowser.connectAsi(payload);
            return;
        }
        if (isComet || !webExtensionEnabled) {
            logger.warn('browserTool: connect_comet_to_asi for the Perplexity extension ignored', {
                requestId: payload.request_id,
                isComet,
                webExtensionEnabled,
            });
            return;
        }
        void connectAsiViaPerplexityExtension(payload).then((result) => {
            if (result.ok) {
                logger.info('browserTool: connect_comet_to_asi delivered to Perplexity extension', {
                    requestId: payload.request_id,
                    version: result.version,
                });
                return;
            }
            logger.warn('browserTool: connect_comet_to_asi not delivered to Perplexity extension', {
                requestId: payload.request_id,
                error: result.error,
            });
        });
    }, [cometBrowser, isComet, webExtensionEnabled]);
    const executeBrowserTool = useCallback(async (browserStep, backend_uuid, thread_url_slug, context_uuid) => {
        logger.info('browserStep', browserStep);
        if (browserStep.browser_open_tab_content) {
            const browserTool = browserStep.browser_open_tab_content;
            if (!browserTool.url || !browserStep.uuid) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            logger.info('browserTool: browser_open_tab', { tool: browserTool }, { request_id: extraHeaders[REQUEST_ID_HEADER] });
            await openTabAsync({
                url: browserTool.url,
                cometBrowser,
                stepUuid: browserStep.uuid,
                extraHeaders: safeJsonParse(browserTool.extra_headers),
                reason,
            });
        }
        else if (browserStep.search_browser_content) {
            const browserTool = browserStep.search_browser_content;
            if (!browserStep.uuid) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            logger.info('browserTool: search_browser', { tool: browserTool }, { request_id: extraHeaders[REQUEST_ID_HEADER] });
            await sendSearchBrowserAsync(browserTool, cometBrowser, browserStep.uuid, extraHeaders, reason);
        }
        else if (browserStep.browser_close_tabs_content) {
            const browserTool = browserStep.browser_close_tabs_content;
            const payload = browserTool.payload;
            if (!payload || !browserStep.uuid) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            logger.info('browserTool: browser_close_tabs', { tool: browserTool }, { request_id: extraHeaders[REQUEST_ID_HEADER] });
            await handleCloseTabs(payload, cometBrowser, browserStep.uuid, extraHeaders, reason);
        }
        else if (browserStep.browser_group_tabs_content) {
            const browserTool = browserStep.browser_group_tabs_content;
            const payload = browserTool.payloads;
            if (!payload || !browserStep.uuid) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            logger.info('browserTool: browser_group_tabs', { tool: browserTool }, { request_id: extraHeaders[REQUEST_ID_HEADER] });
            await handleGroupTabs(payload, cometBrowser, browserStep.uuid, extraHeaders, reason);
        }
        else if (browserStep.entropy_request_content) {
            const browserTool = browserStep.entropy_request_content;
            if (!browserTool.tasks) {
                return;
            }
            const extra_headers = browserTool.extra_headers;
            const requestIdHeader = safeJsonParse(extra_headers)[REQUEST_ID_HEADER];
            logger.info('browserTool: entropy_request', { tool: browserTool }, { request_id: requestIdHeader });
            browserTool.tasks.forEach((task) => {
                logger.info('[comet] Submitting agent task', {
                    task_id: task.uuid,
                    entry_uuid: backend_uuid,
                    request_id: requestIdHeader,
                });
                cometBrowser.submitTask({
                    action: 'startAgentFromPerplexity',
                    type: 'START_AGENT',
                    extra_headers: extra_headers,
                    base_url: browserTool.base_url,
                    entryId: backend_uuid,
                    ...task,
                    use_current_page: task.use_current_page &&
                        (!!import.meta.env.VITE_IS_SIDECAR_BUILD ||
                            !!import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD),
                    // We want to hide screenshots from visible tasks
                    capture_screenshot: !task.use_current_page && !task.tab_id,
                    thread_url_slug: thread_url_slug,
                });
            });
        }
        else if (browserStep.get_url_content_content) {
            const browserTool = browserStep.get_url_content_content;
            if (!browserStep.uuid) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            logger.info('browserTool: get_url_content', { tool: browserTool }, { request_id: extraHeaders[REQUEST_ID_HEADER] });
            if (!isComet)
                return;
            const request = {
                ...browserTool,
                key: browserStep.uuid,
                request_id: extraHeaders[REQUEST_ID_HEADER],
                pages: [...(browserTool.pages || [])],
            };
            const payload = cometBrowser.GetContent(request);
            await sendToolResultAsync(payload, browserStep.uuid, {
                request_id: extraHeaders[REQUEST_ID_HEADER] ?? '',
            }, reason);
        }
        else if (browserStep.browser_ungroup_content) {
            const browserTool = browserStep.browser_ungroup_content;
            if (!browserTool.payload || !browserStep.uuid) {
                return;
            }
            if (!browserTool.payload.group_ids || !browserTool.payload.tab_ids) {
                return;
            }
            const payload = {
                groupIds: browserTool.payload.group_ids,
                tabIds: browserTool.payload.tab_ids,
            };
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            const request = cometBrowser
                .ungroupTabs(payload, browserStep.uuid, extraHeaders[REQUEST_ID_HEADER] ?? '')
                .then((result) => {
                if (!result.is_success)
                    throw result;
                return { group_ids: result.groupIds, tab_ids: result.tabIds };
            });
            await sendToolResultAsync(request, browserStep.uuid, {
                request_id: extraHeaders[REQUEST_ID_HEADER] ?? '',
            }, reason);
        }
        else if (browserStep.browser_search_tab_groups_content) {
            const browserTool = browserStep.browser_search_tab_groups_content;
            const payload = browserTool.payload;
            if (!payload || !browserStep.uuid || !payload.queries) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            const request = cometBrowser
                .searchTabGroups({ ...payload, queries: payload.queries }, browserStep.uuid, extraHeaders[REQUEST_ID_HEADER] ?? '')
                .then((result) => {
                if (!result.is_success)
                    throw result;
                return {
                    results: result.results.map((group) => ({
                        ...group,
                        color: convertBrowserColorToCustom(group.color),
                    })),
                };
            });
            await sendToolResultAsync(request, browserStep.uuid, {
                request_id: extraHeaders[REQUEST_ID_HEADER] ?? '',
            }, reason);
        }
        else if (browserStep.browser_get_visible_tab_screenshot_content) {
            const browserTool = browserStep.browser_get_visible_tab_screenshot_content;
            if (!browserStep.uuid) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            const request = cometBrowser
                .GetVisibleTabScreenshot({
                key: browserStep.uuid,
                request_id: extraHeaders[REQUEST_ID_HEADER] ?? '',
                ...browserTool,
            })
                .then(async (result) => {
                if (!result.data_url) {
                    throw new Error('Failed to capture screenshot - no data URL returned');
                }
                const res = await fetch(result.data_url);
                const blob = await res.blob();
                const file = new File([blob], 'screenshot.' + (browserTool.format ?? 'png'), {
                    type: blob.type,
                });
                const uploadUrl = await createUploadUrl({
                    file,
                    forceImage: true,
                    reason,
                    fileSource: 'entropy',
                });
                if (!uploadUrl || !uploadUrl?.fields || !uploadUrl.s3_bucket_url) {
                    throw new Error('Failed to create upload URL');
                }
                const formData = new FormData();
                Object.entries(uploadUrl.fields).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                formData.append('file', file);
                const response = await fetch(uploadUrl.s3_bucket_url, {
                    method: 'POST',
                    body: formData,
                });
                if (!response.ok) {
                    throw new Error(`Failed to upload screenshot: ${response.statusText}`);
                }
                const s3Result = await response.json();
                if (!s3Result.secure_url) {
                    logger.error('[GetVisibleTabScreenshot] Upload did not return a secure URL', {
                        response: s3Result,
                    });
                    throw new Error('Upload did not return a secure URL');
                }
                return {
                    screenshot_url: s3Result.secure_url,
                };
            });
            await sendToolResultAsync(request, browserStep.uuid, {
                request_id: extraHeaders[REQUEST_ID_HEADER] ?? '',
            }, reason);
        }
        else if (browserStep.connect_comet_to_asi_content) {
            const content = browserStep.connect_comet_to_asi_content;
            logger.info('browserTool: connect_comet_to_asi_content');
            const payload = {
                ws_url: content.ws_url,
                request_id: content.request_id,
                ...content,
                entry_uuid: backend_uuid,
                context_uuid,
            };
            connectAsi(payload);
        }
        else if (browserStep.connect_comet_to_asi) {
            const contents = browserStep.connect_comet_to_asi.connect_comet_to_asi_contents ?? [];
            logger.info('browserTool: connect_comet_to_asi');
            contents.forEach((content) => {
                const payload = {
                    ws_url: content.ws_url,
                    request_id: content.request_id,
                    ...content,
                    entry_uuid: backend_uuid,
                    context_uuid,
                };
                connectAsi(payload);
            });
        }
        else if (browserStep.browser_get_cookies_content) {
            const browserTool = browserStep.browser_get_cookies_content;
            if (!browserStep.uuid) {
                return;
            }
            const extraHeaders = safeJsonParse(browserTool.extra_headers);
            logger.info('browserTool: browser_get_cookies', { tool: browserTool }, { request_id: extraHeaders[REQUEST_ID_HEADER] });
            await sendToolResultAsync(cometBrowser.getCookies(), browserStep.uuid, {
                request_id: extraHeaders[REQUEST_ID_HEADER] ?? '',
            }, reason);
        }
        else {
            logger.error('[useExecuteBrowserTool] Unknown browser tool step', { browserStep }, { reason, backend_uuid });
        }
    }, [cometBrowser, connectAsi, isComet, reason]);
    return useMemo(() => ({
        executeBrowserTool,
    }), [executeBrowserTool]);
}
//# sourceMappingURL=useExecuteBrowserTool.js.map